import { Stack, VStack, Heading, Button, Text, Box, Image, ButtonProps } from '@chakra-ui/react'
import { ChevronRightIcon } from '@src/components/sco/icons'
import { useBreakpointContext } from '@src/components/sco/utilities/BreakpointProvider'
import { NextLink } from '@src/components/sco/utilities/NextLink'
import { ReactNode } from 'react'

interface ComponentCardProps {
  bgColor?: string
  bgGradient?: string
  bgImage?: string
  bgImageGradient?: string
  boxShadow?: string
  border?: string
  btnCTA?: string
  btnColor?: string
  btnHref?: string
  btnProps?: ButtonProps
  btnSize?: 'sm' | 'md' | 'lg' | 'xl' | '2xl'
  btnTextColor?: string
  btnVariant?: ButtonProps['variant']
  cardDescriptionSize?: React.ReactNode
  cardTitle: string
  cardTitleColor?: string
  cardTitleSize?: React.ReactNode
  component?: React.ReactNode
  customDescription?: React.ReactNode
  description?: string | ReactNode
  direction?: 'row' | 'column' | 'row-reverse' | 'column-reverse'
  extras?: React.ReactNode
  features?: React.ReactNode
  flexAlign?: 'flex-start' | 'flex-end' | 'center'
  imageUrl?: string
  removeYPadding?: boolean
  richDescription?: string // HTML string for rich text content
  showImage?: boolean
  textAlign?: 'left' | 'right' | 'center'
}

const ComponentCard: React.FC<ComponentCardProps> = ({
  cardTitle,
  description,
  richDescription,
  direction = 'row',
  bgColor,
  bgGradient,
  btnColor,
  btnCTA,
  btnHref,
  boxShadow,
  border,
  imageUrl,
  component,
  removeYPadding,
  features,
  extras,
  btnTextColor,
  cardTitleSize,
  cardDescriptionSize,
  textAlign = 'left',
  flexAlign = 'flex-start',
  btnSize = 'lg',
  cardTitleColor = 'fg',
  customDescription,
  btnProps,
}) => {
  const { getBreakpointValue } = useBreakpointContext()

  return (
    <Box
      bg={bgGradient}
      bgColor={bgColor}
      borderRadius={getBreakpointValue(['xl', '3xl', '3xl'])}
      boxShadow={boxShadow}
      padding={getBreakpointValue([4, 10])}
      paddingY={removeYPadding ? 0 : getBreakpointValue([4, 10])}
      width={'full'}
    >
      <VStack width={'full'}>
        <Stack align={'center'} direction={direction} gap={5} height={'full'} width={'full'}>
          <VStack
            align={flexAlign}
            flex={2}
            gap={5}
            padding={getBreakpointValue([4, 10])}
            paddingX={removeYPadding ? 0 : getBreakpointValue([4, 10])}
          >
            <VStack align={flexAlign} gap={getBreakpointValue([3, 5])}>
              <Heading
                color={cardTitleColor}
                size={cardTitleSize || getBreakpointValue(['xl', '3xl', '4xl'])}
                textAlign={textAlign}
              >
                {cardTitle}
              </Heading>
              {customDescription ? (
                customDescription
              ) : richDescription ? (
                <Text
                  dangerouslySetInnerHTML={{ __html: richDescription }}
                  fontSize={cardDescriptionSize || getBreakpointValue(['sm', 'md', 'xl'])}
                  textAlign={textAlign}
                />
              ) : (
                <Text
                  color={'fg.muted'}
                  fontFamily={'longform'}
                  fontSize={cardDescriptionSize || getBreakpointValue(['sm', 'md', 'xl'])}
                  textAlign={textAlign}
                >
                  {description}
                </Text>
              )}
            </VStack>

            {btnCTA && (
              <Button
                bgColor={btnColor}
                color={btnTextColor}
                marginTop={getBreakpointValue([0, 0, 0, 4])}
                size={btnSize}
                {...btnProps}
                asChild
              >
                <NextLink href={btnHref ?? ''}>
                  {btnCTA ? btnCTA : 'Learn more'}
                  <ChevronRightIcon />
                </NextLink>
              </Button>
            )}
          </VStack>
          {component ? (
            component
          ) : (
            <Image alt={cardTitle} height={'full'} maxWidth={'600px'} src={imageUrl} width={'full'} />
          )}
        </Stack>
        {features}
        <Box width={'full'}>{extras}</Box>
      </VStack>
    </Box>
  )
}

export default ComponentCard
